import styled, { css } from 'styled-components';
import { device } from '../../../../styles/devices';
import { COLORS, SIZING } from '../../../../styles/variables';
import { AlphaTheme } from '../../../../../design-system/ThemeProvider/alphaTheme';
import { SYS_COLOR_FOREGROUND_ON_FORMFIELDS_SUBDUED_DARK } from '@oneaudi/unified-web-alpha-common-v2';

interface StyleProps {
  visible?: boolean;
}

export const StyledInput = styled.input<{ error?: boolean }>`
  font-family: Audi Type;
  width: 100%;
  border: none;
  background-color: transparent;
  border-bottom: ${({ theme, error }: { theme: Partial<AlphaTheme>; error?: boolean }) =>
    theme.canvas200 ? 'none' : `1px solid ${error ? COLORS.error : COLORS.grey60}`};
  padding-left: ${SIZING.unit(4.5)};
  padding-right: ${({ theme }: { theme: Partial<AlphaTheme>; error?: boolean }) =>
    theme.canvas200 ? '44px' : SIZING.unit(3)};
  font-size: 16px;
  :focus-visible {
    outline: none;
  }
  color: ${({ theme }: { theme: Partial<AlphaTheme> }) =>
    theme.foregroundNeutral100 ?? COLORS.grey80};
  &::placeholder {
    // ASK
    font-family:
      Audi Type,
      sans-serif;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.002px;
    font-size: 16px;
    color: ${({ theme }: { theme: Partial<AlphaTheme> }) =>
      theme.foregroundNeutral200 ?? COLORS.grey80};
    @media ${device.tablet} {
      color: ${({ theme }: { theme: Partial<AlphaTheme> }) =>
        theme.foregroundNeutral200 ?? COLORS.grey80};
      line-height: 24px;
      letter-spacing: 0.003px;
    }
    @media (max-width: 926px) and (max-height: 600px) and (orientation: landscape) {
      color: ${COLORS.white};
    }
  }
`;

export const SearchIconContainer = styled.div`
  position: absolute;
  height: 100%;
  display: flex;
  align-items: center;
`;

export const ClearInputWrapper = styled.div<StyleProps>`
  position: absolute;
  height: 100%;
  display: flex;
  align-items: center;
  top: 0;
  right: ${({ theme }: StyleProps & { theme: Partial<AlphaTheme> }) =>
    theme.canvas200 ? '16px' : '0'};
  padding-left: ${({ theme }: StyleProps & { theme: Partial<AlphaTheme> }) =>
    theme.canvas200 ? '12px' : '0'};
  visibility: hidden;
  cursor: pointer;
  color: ${({ theme }: StyleProps & { theme: Partial<AlphaTheme> }) =>
    theme.foregroundOnFormfieldsSubdued ?? 'inherit'};
  ${({ visible }: StyleProps) => {
    if (visible)
      return css`
        visibility: visible;
      `;
    return '';
  }};
`;

interface ErrorWrapperProps extends StyleProps {
  isAlpha?: boolean;
}

export const ErrorWrapper = styled.div<ErrorWrapperProps>`
  margin-top: ${SIZING.unit(1.5)};
  display: none;
  align-items: ${({ isAlpha }: ErrorWrapperProps) => (isAlpha ? 'flex-start' : 'center')};
  font-family: Audi Type;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.002px;
  gap: 8px;
  color: ${({ isAlpha }: ErrorWrapperProps) =>
    isAlpha ? SYS_COLOR_FOREGROUND_ON_FORMFIELDS_SUBDUED_DARK : COLORS.error};
  svg {
    min-width: fit-content;
    flex-shrink: 0;
    ${({ isAlpha }: ErrorWrapperProps) =>
      isAlpha
        ? css`
            margin-top: 6px;
            color: ${COLORS.error};
          `
        : ''}
  }
  ${({ visible }: ErrorWrapperProps) => {
    if (visible)
      return css`
        display: flex;
      `;
    return '';
  }};
`;

export const InputFieldWrapper = styled.div<{ error?: boolean }>`
  display: flex;
  align-items: center;
  position: relative;
  ${({ theme, error }: { theme: Partial<AlphaTheme>; error?: boolean }) =>
    theme.canvas200 &&
    css`
      border-radius: ${theme.borderRadiusMax ?? '999px'};
      border: 1px solid ${error ? COLORS.error : theme.formFieldStroke};
      background: ${theme.formFieldFill};
      padding: 12px ${theme.spaceFixed200 ?? '16px'};
    `}
`;

export const SuggestionsContainer = styled.ul`
  position: absolute;
  top: 100%;
  margin: 0;
  border-radius: 8px;
  padding: ${SIZING.unit(0.625)} 0px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  border-radius: 8px;
  border: 0.5px solid
    ${({ theme }: { theme: Partial<AlphaTheme> }) =>
      theme.menuBackgroundDark ? theme.borderSubtle : COLORS.menuBorder};
  background: ${({ theme }: { theme: Partial<AlphaTheme> }) =>
    theme.menuBackgroundDark ?? COLORS.menuBackground};
  width: 100%;
  box-shadow:
    0px 2px 12px 0px rgba(0, 0, 0, 0.15),
    0px 0px 6px 0px rgba(0, 0, 0, 0.05) inset;
  z-index: 5;
`;

export const Option = styled.li<{ focused?: boolean }>`
  width: 100%;
  background-color: ${({ focused, theme }: { focused?: boolean; theme: Partial<AlphaTheme> }) => {
    if (!focused) return '';
    return theme.elevationFillStopDark ?? COLORS.grey70;
  }};
  display: flex;
  align-items: flex-start;
  padding: ${SIZING.unit(0.125)} ${SIZING.unit(0.5)};
  border-radius: 8px;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  font-family: 'Audi Type', sans-serif;
  cursor: pointer;
  color: ${({ focused, theme }: { focused?: boolean; theme: Partial<AlphaTheme> }) =>
    theme.foregroundNeutral100 ?? (focused ? COLORS.white : COLORS.menuTextColor)};
`;

export const CheckMarkWrapper = styled.span<{ focused?: boolean }>`
  visibility: ${(props) => (props.focused ? '' : 'hidden')};
  width: 18px;
  height: 18px;
  padding-top: ${SIZING.unit(0.5)};
`;

export const Container = styled.div``;
